import React, { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import GooglePlacesAutocomplete, {
	geocodeByPlaceId,
} from "react-google-places-autocomplete";
import {
	CCButton,
	CCCombobox,
	CCField,
	CCModal,
	CCSpinner,
	CCSwitch,
	CCCheckbox,
	useToast,
} from "@pymblecitizencoders/cc-ui";
import { toDatePart, toIso, toTimePart } from "../../utils/eventDates";
import {
	ALL_DAY_END,
	ALL_DAY_START,
	RequestDraft,
	STEP_LABELS,
	StepKey,
	addMinutes,
	emptyDraft,
	stepError,
	STEPS,
	submitChecklist,
} from "../../utils/eventRequest";
import { app } from "../../config";
import { useEventTypes } from "../../hooks/useEventTypes";
import { useRooms } from "../../hooks/useRooms";
import { useConcurrentEvents } from "../../hooks/useConcurrentEvents";
import {
	publishStatusLabel,
	refusalDetail,
	usePublishWorkflow,
} from "../../hooks/useEventPublish";
import { DateTimeRow } from "./EventCreateDialog";
import { EventClashPanel } from "./EventClashPanel";
import "./eventManager.css";

interface EventRequestWizardProps {
	open: boolean;
	/** The instant the person started from, local-naive. See EventCreateDialog. */
	initialDate: string;
	initialEnd?: string;
	saving: boolean;
	onClose: () => void;
	/**
	 * Creates the event and writes the answers onto it, resolving to its id (or
	 * null when the API did not say). A failure rejects, so the wizard stays on
	 * the review step with everything typed.
	 */
	onRequest: (draft: RequestDraft) => Promise<string | null>;
	/** Open the finished event in the editor. */
	onOpenEvent: (id: string) => void;
}

/** What the address autocomplete hands back on selection. */
interface PlaceSelection {
	/** The option's own text; the suggestion's description. */
	label?: string;
	value?: {
		place_id?: string;
		description?: string;
		structured_formatting?: { main_text?: string };
	};
}

const DEFAULT_DURATION_MINUTES = 60;
/** How often the status screen re-asks while the request is with approvers. */
const POLL_MS = 30_000;

const TickField: React.FC<{
	id: string;
	label: string;
	checked: boolean;
	disabled: boolean;
	onChange: (v: boolean) => void;
}> = ({ id, label, checked, disabled, onChange }) => (
	<CCCheckbox
		id={id}
		label={label}
		checked={checked}
		disabled={disabled}
		onChange={(e) => onChange(e.target.checked)}
	/>
);

const Choice: React.FC<{
	name: string;
	legend: string;
	value: string;
	options: { value: string; label: string }[];
	disabled: boolean;
	onChange: (v: string) => void;
}> = ({ name, legend, value, options, disabled, onChange }) => (
	<fieldset className="em-create-categories">
		<legend className="em-label">{legend}</legend>
		{options.map((o) => (
			<label key={o.value} className="em-create-category">
				<input
					type="radio"
					name={name}
					checked={value === o.value}
					disabled={disabled}
					onChange={() => onChange(o.value)}
				/>
				{o.label}
			</label>
		))}
	</fieldset>
);

/**
 * A request for a new event, asked one topic at a time.
 *
 * It follows the school's pre-approval form: what and when, what it is for,
 * where, then the questions the Calendar Group needs. The event is created when
 * the last step is submitted and goes straight into the sign-off chain; the
 * editor opens once the chain has published it. Until then the status screen is
 * the answer to "where is my request".
 * */
export const EventRequestWizard: React.FC<EventRequestWizardProps> = ({
	open,
	initialDate,
	initialEnd,
	saving,
	onClose,
	onRequest,
	onOpenEvent,
}) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const { types: eventTypes } = useEventTypes();
	const steps = STEPS;
	const [index, setIndex] = useState(0);
	const [draft, setDraft] = useState<RequestDraft>(emptyDraft);
	const [error, setError] = useState("");
	const [busy, setBusy] = useState(false);
	/** Checklist items ticked on the last step. */
	const [ticked, setTicked] = useState<string[]>([]);
	/** Set once the event exists; from then on the wizard is a status screen. */
	const [eventId, setEventId] = useState<string | null>(null);
	const submittedFor = useRef<string | null>(null);

	const { workflow, workflowLoading, submit } = usePublishWorkflow(eventId);

	/* The directory is only fetched once an onsite event is chosen. If it is
	   empty or cannot be loaded the step falls back to a typed location, so a
	   request is never blocked on the room list. */
	const rooms = useRooms(open && draft.venue === "onsite");
	const pickRoom = rooms.loaded && !rooms.failed && rooms.rooms.length > 0;

	const patch = (next: Partial<RequestDraft>) => {
		setDraft((d) => ({ ...d, ...next }));
		setError("");
	};

	// Reset on every open, so an abandoned request never reappears against a
	// different day.
	useEffect(() => {
		if (!open) return;
		const day = toDatePart(initialDate);
		const clicked = toTimePart(initialDate);
		const timed = !!clicked && clicked !== ALL_DAY_START;
		const draggedDay = toDatePart(initialEnd ?? "");
		const draggedTime = toTimePart(initialEnd ?? "");
		setDraft({
			...emptyDraft(),
			allDay: !timed,
			startDate: day,
			startTime: timed ? clicked : ALL_DAY_START,
			endDate: draggedDay || day,
			endTime: draggedDay
				? timed
					? draggedTime
					: ALL_DAY_END
				: timed
					? addMinutes(clicked, DEFAULT_DURATION_MINUTES)
					: ALL_DAY_END,
		});
		setIndex(0);
		setError("");
		setBusy(false);
		setTicked([]);
		setEventId(null);
		submittedFor.current = null;
	}, [open, initialDate, initialEnd]);

	const start = toIso(
		draft.startDate,
		draft.allDay ? ALL_DAY_START : draft.startTime,
	);
	const end = toIso(
		draft.endDate || draft.startDate,
		draft.allDay ? ALL_DAY_END : draft.endTime,
	);
	const step = steps[index];
	const clashes = useConcurrentEvents(start, end, open && step === "when");

	// The event exists; send it to its approvers exactly once.
	useEffect(() => {
		if (!eventId || submittedFor.current === eventId) return;
		submittedFor.current = eventId;
		void submit().catch((e) => {
			toast.show({
				message:
					refusalDetail(e) ??
					"Could not send this for sign-off. Open the event and submit it there.",
				severity: "danger",
				duration: 6000,
			});
		});
		// `submit` is a fresh function each render; the ref is the guard.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [eventId]);

	// While it is with approvers, ask again now and then.
	useEffect(() => {
		if (!open || !eventId) return;
		const timer = setInterval(
			() =>
				queryClient.invalidateQueries({
					queryKey: ["publish", "workflow", eventId],
				}),
			POLL_MS,
		);
		return () => clearInterval(timer);
	}, [open, eventId, queryClient]);

	/* Choosing a suggestion fills the name, address and coordinates together.
	   Typing instead keeps the text as the location and drops the address and
	   coordinates, which belonged to a place the field no longer says. */
	const onPlace = async (place: PlaceSelection | null) => {
		/* The option's `label` is the description too, so it covers a suggestion
		   whose `value` is not shaped the way the autocomplete's types say. */
		const description = place?.value?.description ?? place?.label ?? "";
		if (!place || !description) return;
		/* Fill the field FIRST, from what the suggestion already says. The
		   coordinates need a second call to the geocoder, which can fail or be
		   slow, and the field staying as typed until it answered read as the
		   choice not having taken. */
		patch({
			locationName:
				place.value?.structured_formatting?.main_text ?? description,
			locationAddress: description,
			locationLat: null,
			locationLon: null,
		});
		const placeId = place.value?.place_id;
		if (!placeId) return;
		try {
			const [result] = await geocodeByPlaceId(placeId);
			const point = result?.geometry?.location;
			if (!point) return;
			patch({
				locationLat: String(point.lat()),
				locationLon: String(point.lng()),
			});
		} catch {
			// No detail logged: the geocoder echoes the query back. The address is
			// already saved; only the map pin is missing.
		}
	};

	const next = () => {
		const problem = stepError(step, draft);
		if (problem) return setError(problem);
		setError("");
		setIndex((i) => Math.min(i + 1, steps.length - 1));
	};

	const submitRequest = async () => {
		if (!allTicked) return;
		const problem = steps
			.map((s) => ({ s, e: stepError(s, draft) }))
			.find((x) => x.e);
		if (problem) {
			setIndex(steps.indexOf(problem.s));
			return setError(problem.e);
		}
		setBusy(true);
		try {
			const id = await onRequest(draft);
			if (id) setEventId(id);
			else onClose();
		} catch {
			// No detail surfaced: API errors can echo the request back.
			toast.show({
				message: "Could not create the event. Please try again.",
				severity: "danger",
				duration: 5000,
			});
		} finally {
			setBusy(false);
		}
	};

	const locked = saving || busy;
	const status = workflow?.Status ?? null;
	const published = status === "published";

	if (eventId) {
		return (
			<CCModal
				open={open}
				onClose={onClose}
				title="Request submitted"
				footer={
					<>
						<CCButton variant="outline" onClick={onClose}>
							Close
						</CCButton>
						<CCButton
							variant="solid"
							colour="primary"
							disabled={!published}
							onClick={() => onOpenEvent(eventId)}
						>
							Open event
						</CCButton>
					</>
				}
			>
				<div className="em-request-status">
					<strong>{draft.title.trim()}</strong>
					{workflowLoading || !status ? (
						<CCSpinner size="sm" label="Checking sign-off..." />
					) : (
						<span className={`em-approve-status is-${status}`}>
							{publishStatusLabel(status)}
						</span>
					)}
				</div>
			</CCModal>
		);
	}

	const yn = (v: boolean) => (v ? "Yes" : "No");
	const typeName = eventTypes.find((t) => t.Code === draft.eventType);
	const when = (iso: string) =>
		new Date(iso).toLocaleString(
			"en-AU",
			draft.allDay
				? { dateStyle: "medium" }
				: { dateStyle: "medium", timeStyle: "short" },
		);
	/** Every answer, in the order it was asked, for the last step. */
	const summary: [string, string][] = [
		["Event", draft.title.trim()],
		["Event type", typeName ? (typeName.Name ?? typeName.Code) : "None"],
		["Starts", when(start)],
		["Ends", when(end)],
		["Urgent", yn(draft.urgent)],
		["Purpose", draft.purpose.trim()],
		["Further details", draft.details.trim()],
		["Venue", draft.venue === "offsite" ? "Offsite" : "Onsite"],
		["Location", draft.locationName.trim()],
		...(draft.locationAddress
			? ([["Address", draft.locationAddress]] as [string, string][])
			: []),
		["Boarding students involved", yn(draft.boarding)],
		...(draft.boarding
			? ([["Boarding team informed", yn(draft.kmAcknowledged)]] as [
					string,
					string,
				][])
			: []),
		["Assessments checked", yn(draft.assessmentsChecked)],
		["Representative pathway", yn(draft.pathway)],
		["Charged", yn(draft.charged)],
		...(draft.charged
			? ([["How it is charged", draft.howCharged.trim()]] as [string, string][])
			: []),
		["Staff cover needed", yn(draft.staffCover)],
		["Cost budgeted", yn(draft.budgeted)],
	];

	const checklist = submitChecklist(draft);
	const allTicked = checklist.every((item) => ticked.includes(item));

	const body = (() => {
		switch (step as StepKey) {
			case "when":
				return (
					<div className="em-create-cols">
						<div className="em-create-form">
							<CCField
								label="Event title"
								type="text"
								fullWidth
								value={draft.title}
								disabled={locked}
								onChange={(e) => patch({ title: e.target.value })}
							/>
							<div className="em-request-type-row">
								<CCCombobox
									label="Event type"
									fullWidth
									disabled={locked}
									options={eventTypes.map((t) => ({
										value: t.Code,
										label: t.Name ?? t.Code,
									}))}
									value={draft.eventType}
									onChange={(value) => patch({ eventType: value || null })}
								/>
								<CCCheckbox
									id="em-request-urgent"
									label="Urgent"
									checked={draft.urgent}
									disabled={locked}
									onChange={(e) => patch({ urgent: e.target.checked })}
								/>
							</div>
							<div className="em-grid">
								<div className="em-col-md-6">
									<DateTimeRow
										label="Starts"
										date={draft.startDate}
										time={draft.startTime}
										showTime={!draft.allDay}
										disabled={locked}
										onDate={(v) =>
											patch({
												startDate: v,
												endDate:
													v && draft.endDate && draft.endDate < v
														? v
														: draft.endDate,
											})
										}
										onTime={(v) => patch({ startTime: v })}
									/>
								</div>
								<div className="em-col-md-6">
									<DateTimeRow
										label="Ends"
										date={draft.endDate}
										time={draft.endTime}
										showTime={!draft.allDay}
										disabled={locked}
										min={draft.startDate}
										onDate={(v) => patch({ endDate: v })}
										onTime={(v) => patch({ endTime: v })}
									/>
								</div>
							</div>
							<CCSwitch
								id="em-request-all-day"
								label="All day"
								checked={draft.allDay}
								disabled={locked}
								onChange={(allDay) => {
									clashes.refresh();
									if (allDay) {
										patch({
											allDay,
											startTime: ALL_DAY_START,
											endTime: ALL_DAY_END,
										});
										return;
									}
									const from = `${String(new Date().getHours()).padStart(2, "0")}:00`;
									patch({
										allDay,
										startTime: from,
										endTime: addMinutes(from, DEFAULT_DURATION_MINUTES),
									});
								}}
							/>
						</div>
						<EventClashPanel
							events={clashes.events}
							loading={clashes.loading}
							failed={clashes.failed}
							tooWide={clashes.tooWide}
							spanDays={clashes.spanDays}
						/>
					</div>
				);
			case "about":
				return (
					<div className="em-create-form">
						<CCField
							label="What is the purpose of the event?"
							multiline
							rows={3}
							fullWidth
							value={draft.purpose}
							disabled={locked}
							onChange={(e) => patch({ purpose: e.target.value })}
						/>
						<CCField
							label="Further details"
							multiline
							rows={6}
							fullWidth
							value={draft.details}
							disabled={locked}
							onChange={(e) => patch({ details: e.target.value })}
						/>
					</div>
				);
			case "where":
				return (
					<div className="em-create-form em-request-where">
						<Choice
							name="em-request-venue"
							legend="Onsite or offsite?"
							value={draft.venue}
							options={[
								{ value: "onsite", label: "Onsite" },
								{ value: "offsite", label: "Offsite" },
							]}
							disabled={locked}
							onChange={(v) =>
								// A room chosen for one venue is wrong for the other.
								patch({
									venue: v as RequestDraft["venue"],
									locationName: "",
									roomKey: null,
									locationAddress: "",
									locationLat: null,
									locationLon: null,
								})
							}
						/>
						{draft.venue === "onsite" && pickRoom ? (
							<CCCombobox
								label="Which room?"
								fullWidth
								disabled={locked}
								options={rooms.rooms.map((r) => ({
									value: r.key,
									label: r.building ? `${r.name} (${r.building})` : r.name,
								}))}
								value={draft.roomKey}
								onChange={(key) => {
									const room = rooms.rooms.find((r) => r.key === key);
									patch({
										roomKey: room ? room.key : null,
										locationName: room ? room.name : "",
									});
								}}
							/>
						) : draft.venue === "offsite" && app.googleMapsApiKey ? (
							<div className="em-address-search">
								<label className="em-label" htmlFor="em-request-address">
									Where will it be held?
								</label>
								<div className="em-address-search__field">
									<GooglePlacesAutocomplete
										apiKey={app.googleMapsApiKey}
										selectProps={{
											inputId: "em-request-address",
											classNamePrefix: "em-places",
											placeholder: "Start typing an address…",
											value: null,
											inputValue: draft.locationAddress || draft.locationName,
											controlShouldRenderValue: false,
											isDisabled: locked,
											blurInputOnSelect: true,
											onInputChange: (
												text: string,
												meta: { action: string },
											) => {
												if (meta.action !== "input-change") return;
												patch({
													locationName: text,
													locationAddress: "",
													locationLat: null,
													locationLon: null,
												});
											},
											onChange: (place: unknown) =>
												void onPlace(place as PlaceSelection),
											"aria-label": "Where will it be held?",
										}}
									/>
								</div>
							</div>
						) : (
							<CCField
								label={
									draft.venue === "offsite"
										? "Where will it be held?"
										: "Where would you like it held?"
								}
								type="text"
								fullWidth
								value={draft.locationName}
								disabled={locked}
								onChange={(e) => patch({ locationName: e.target.value })}
							/>
						)}
						{draft.venue === "onsite" && (
							<p className="em-muted em-request-note">
								The room is not booked until the Calendar Committee approves the
								event.
							</p>
						)}
					</div>
				);
			case "students":
				return (
					<div className="em-create-form">
						<TickField
							id="em-request-boarding"
							label="Boarding students are involved"
							checked={draft.boarding}
							disabled={locked}
							onChange={(v) => patch({ boarding: v })}
						/>
						{draft.boarding && (
							<CCCheckbox
								id="em-request-km"
								label="I will inform the Boarding team"
								checked={draft.kmAcknowledged}
								disabled={locked}
								onChange={(e) => patch({ kmAcknowledged: e.target.checked })}
							/>
						)}
						<TickField
							id="em-request-assess"
							label="I have checked for assessments at the same time"
							checked={draft.assessmentsChecked}
							disabled={locked}
							onChange={(v) => patch({ assessmentsChecked: v })}
						/>
						<TickField
							id="em-request-pathway"
							label="Representative pathway or qualifying event"
							checked={draft.pathway}
							disabled={locked}
							onChange={(v) => patch({ pathway: v })}
						/>
					</div>
				);
			case "cost":
				return (
					<div className="em-create-form">
						<TickField
							id="em-request-charged"
							label="There is a charge"
							checked={draft.charged}
							disabled={locked}
							onChange={(v) => patch({ charged: v })}
						/>
						{draft.charged && (
							<CCField
								label="How is it charged?"
								type="text"
								fullWidth
								maxLength={500}
								value={draft.howCharged}
								disabled={locked}
								onChange={(e) => patch({ howCharged: e.target.value })}
							/>
						)}
						<TickField
							id="em-request-cover"
							label="Cover is needed for the supervising staff"
							checked={draft.staffCover}
							disabled={locked}
							onChange={(v) => patch({ staffCover: v })}
						/>
						<TickField
							id="em-request-budget"
							label="Any cost is already budgeted"
							checked={draft.budgeted}
							disabled={locked}
							onChange={(v) => patch({ budgeted: v })}
						/>
					</div>
				);
			default:
				return (
					<>
						<dl className="em-request-review">
							{summary.map(([label, value]) => (
								<React.Fragment key={label}>
									<dt>{label}</dt>
									<dd>{value}</dd>
								</React.Fragment>
							))}
						</dl>
						<div className="em-request-checklist">
							{checklist.map((item, i) => (
								<CCCheckbox
									key={item}
									id={`em-request-check-${i}`}
									label={item}
									checked={ticked.includes(item)}
									disabled={locked}
									onChange={(e) =>
										setTicked((t) =>
											e.target.checked
												? [...t, item]
												: t.filter((x) => x !== item),
										)
									}
								/>
							))}
						</div>
					</>
				);
		}
	})();

	const last = index === steps.length - 1;

	return (
		<CCModal
			open={open}
			onClose={onClose}
			title={`New event: ${STEP_LABELS[step]} (${index + 1} of ${steps.length})`}
			className="em-create-modal"
			footer={
				<>
					{index > 0 && (
						<CCButton
							className="em-request-back"
							variant="outline"
							onClick={() => {
								setError("");
								setIndex(index - 1);
							}}
							disabled={locked}
						>
							Back
						</CCButton>
					)}
					<CCButton
						variant="solid"
						colour="primary"
						disabled={locked || (last && !allTicked)}
						onClick={last ? () => void submitRequest() : next}
					>
						{last ? (locked ? "Submitting…" : "Submit for approval") : "Next"}
					</CCButton>
				</>
			}
		>
			{body}
			{error && (
				<span className="em-error" role="alert">
					{error}
				</span>
			)}
		</CCModal>
	);
};
